// `StageDocx` (SuiteCompile/StagePlayOutputs.swift): a play as Word — the same elements as the PDF, each a paragraph
// style of its own (Character, Dialogue, Stage Direction…) so the document reflows, the title, cast and setting pages
// first without page numbers, then the play numbered at the top right: act–scene–page in a US play's acts (one Word
// section per scene), plain in a UK play.
import { fountainParagraphText } from '@writing-suite/core';
import { DOCX_SETTINGS, docxApp, docxCore, docxCustom, twips, whole } from '../docx.ts';
import { bylineName, contactName, isPenName, pageInches } from '../manuscript.ts';
import type { CompiledManuscript, RenderSettings } from '../types.ts';
import { xmlEscape } from '../xml.ts';
import { ZipWriter } from '../zip.ts';
import { count, run, speaker, type Paragraph, type Run } from './script.ts';
import { continuations, directionLabel, numbersPage, printedStageScript, stagePageNumber, type StagePart, type StageScript } from './stage.ts';

const TAB = '<w:r><w:tab/></w:r>';
const LINE_BREAK = '<w:r><w:br/></w:r>';
const PAGE_BREAK = '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';
const SPEECH = new Set(['dialogue', 'parenthetical', 'lyric']);

/** The Word parts, in the order the Swift adds them. `script`: the play as printed (a UK sample's cut found by Typst). */
export function stageDocxEntries(m: CompiledManuscript, o: { date?: Date; properties?: Record<string, string>; script?: StageScript } = {}): [string, string][] {
  const script = o.script ?? printedStageScript(m);
  const r = m.preset.render;
  const uk = r.screenplay?.format === 'stage-uk';
  const style = r.screenplay?.pageNumbers;
  const { width, height } = pageInches(r.page.size);
  const left = uk ? r.page.margins : Math.max(r.page.margins, 1.5);
  const properties = o.properties ?? {};
  const hasProperties = Object.keys(properties).length > 0;

  const sectPr = (header: number | null, restart: boolean) => {
    const margins = `<w:pgMar w:top="${twips(r.page.margins)}" w:right="${twips(r.page.margins)}" w:bottom="${twips(r.page.margins)}" w:left="${twips(left)}" w:header="${twips(0.5)}" w:footer="${twips(0.5)}" w:gutter="0"/>`;
    return `<w:sectPr>${header !== null ? `<w:headerReference w:type="default" r:id="rIdHeader${header}"/>` : ''}`
      + `<w:type w:val="nextPage"/><w:pgSz w:w="${twips(width)}" w:h="${twips(height)}"/>${margins}`
      + (restart ? '<w:pgNumType w:start="1"/>' : '') + '</w:sectPr>';
  };
  const ending = (sect: string) => `<w:p><w:pPr>${sect}</w:pPr></w:p>`;

  let body = '';
  const headers: string[] = [];
  // The pages before the play.
  const front = frontMatter(script, m, uk);
  body += front;
  if (front !== '' && script.parts.length) body += ending(sectPr(null, false));
  // The play: a header per scene when its number names the scene, else one for the lot.
  const perPart = !uk && style !== 'plain' && script.parts.some((p) => p.act !== null);
  let finalSect = sectPr(null, false);
  script.parts.forEach((part, index) => {
    body += uk ? ukPart(part, script.musical) : usPart(part);
    let header: number;
    if (perPart) {
      const prefix = stagePageNumber(script, 0, index, style).slice(0, -1); // "II-1-" without the page
      headers.push(prefix);
      header = headers.length;
    } else {
      if (!headers.length) headers.push('');
      header = 1;
    }
    const sect = sectPr(header, index === 0);
    if (index === script.parts.length - 1) finalSect = sect; else body += ending(sect);
  });
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>${body}${finalSect}</w:body></w:document>`;

  const parts: [string, string][] = [
    ['[Content_Types].xml', contentTypes(headers.length, hasProperties)],
    ['_rels/.rels', rootRels(hasProperties)],
    ['word/document.xml', document],
    ['word/styles.xml', styles(r, uk, m.language, width - left - r.page.margins)],
    ['word/settings.xml', DOCX_SETTINGS],
    ['word/_rels/document.xml.rels', documentRels(headers.length)],
  ];
  headers.forEach((prefix, i) => parts.push([`word/header${i + 1}.xml`, header(prefix, !uk && !perPart)]));
  parts.push(['docProps/core.xml', docxCore(m, o.date ?? new Date())], ['docProps/app.xml', docxApp(m)]);
  if (hasProperties) parts.push(['docProps/custom.xml', docxCustom(properties)]);
  return parts;
}

/** `StageDocx.write`. */
export function writeStageDocx(m: CompiledManuscript, o: { date?: Date; properties?: Record<string, string>; script?: StageScript } = {}): Uint8Array {
  const zip = new ZipWriter();
  for (const [name, text] of stageDocxEntries(m, o)) zip.add(name, text);
  return zip.finish();
}

// MARK: Pages before the play

function frontMatter(script: StageScript, m: CompiledManuscript, uk: boolean): string {
  let out = '';
  let pageStarted = false;
  const newPage = () => { const started = pageStarted; pageStarted = true; return started; };
  if (m.preset.render.titlePage.include) {
    const field = (key: string) => script.titlePage.find((f) => f.key === key)?.value;
    let lines: string[];
    if (script.titleLines !== null && field('title') === undefined) {
      lines = script.titleLines.map((l, i) => (i === 0 ? l.toUpperCase() : l));
    } else {
      lines = [(field('title') ?? m.title).toUpperCase(), field('credit') ?? script.description];
      const author = field('author') ?? field('authors') ?? (m.preset.render.titlePage.byline && m.author ? bylineName(m.author) : undefined);
      if (field('author') === undefined && field('authors') === undefined && m.preset.render.titlePage.byline && script.credits !== null) lines.push(...script.credits);
      else if (author !== undefined) lines.push('by', author);
    }
    lines.forEach((line, i) => { out += paragraph(i === 0 ? 'PlayTitle' : 'Byline', text(line, i === 0 && uk), false, i === 0 && newPage()); });
    let contact = field('contact');
    if (contact === undefined && m.preset.render.titlePage.contact && m.author && m.author.contact.length) {
      contact = [...(isPenName(m.author) ? [contactName(m.author)] : []), ...m.author.contact].join('\n');
    }
    (contact ?? '').split('\n').filter((l) => l !== '').forEach((line, i) => { out += paragraph(i === 0 ? 'ContactFirst' : 'Contact', text(line)); });
  }
  if (script.cast.length || script.castNotes.length) {
    out += paragraph('PageHeading', text('CHARACTERS', uk), false, newPage());
    for (const entry of script.cast) out += paragraph('CastEntry', text(entry.name, uk) + (entry.description === '' ? '' : TAB + text(entry.description)));
    for (const note of script.castNotes) out += paragraph('CastNote', text(note));
  }
  if (script.musicalNumbers !== null) {
    out += paragraph('PageHeading', text('MUSICAL NUMBERS', uk), false, newPage());
    for (const line of script.musicalNumbers) out += paragraph('CastNote', text(line));
  } else if (script.songs.length) {
    out += paragraph('PageHeading', text('MUSICAL NUMBERS', uk), false, newPage());
    for (const group of numbersPage(script)) {
      if (group.act !== null) out += paragraph('SettingHeading', text(group.act, uk));
      for (const song of group.songs) {
        out += paragraph('SongEntry', text(`${song.number}. ${song.title}`) + (song.singers.length ? TAB + text(song.singers.join(', ')) : ''));
      }
    }
  }
  if (script.setting.length) {
    out += paragraph('PageHeading', text('SETTING', uk), false, newPage());
    for (const line of script.setting) {
      const heading = count(line) <= 24 && !/[.,;!?]/.test(line);
      out += paragraph(heading ? 'SettingHeading' : 'CastNote', text(heading ? line.toUpperCase() : line, heading && uk));
    }
  }
  return out;
}

// MARK: The play

/** A US scene: its headings, then each element as `stageLayout` sets it. */
function usPart(part: StagePart): string {
  let out = part.headings.map((h) => paragraph('ActHeading', text(h))).join('');
  const ps = part.paragraphs;
  let i = 0;
  while (i < ps.length) {
    const p = ps[i];
    switch (p.element) {
      case 'character': {
        out += paragraph('Character', runs(p.lines[0] ?? [], false, '^'));
        let j = i + 1;
        while (j < ps.length && SPEECH.has(ps[j].element)) {
          const q = ps[j];
          const last = j + 1 >= ps.length || !SPEECH.has(ps[j + 1].element);
          const s = q.element === 'parenthetical' ? 'Parenthetical' : q.element === 'lyric' ? 'Lyric' : 'Dialogue';
          out += paragraph(s, q.lines.map((l) => runs(l)).join(LINE_BREAK), last);
          j += 1;
        }
        i = j;
        continue;
      }
      case 'action': {
        const labelled = p.lines.length ? directionLabel(p.lines[0]) : null;
        if (labelled) {
          out += paragraph('SettingLabel', text(labelled.label) + TAB + [labelled.rest, ...p.lines.slice(1)].map((l) => runs(l)).join(LINE_BREAK), true);
        } else {
          out += paragraph('StageDirection', bracketed(p.lines).map((l) => runs(l)).join(LINE_BREAK), true);
        }
        break;
      }
      case 'transition': out += paragraph('Transition', runs(p.lines[0] ?? []), true); break;
      case 'pageBreak': out += PAGE_BREAK; break;
      case 'centered': out += paragraph(isSongHeading(p) ? 'Song' : 'Centred', p.lines.map((l) => runs(l)).join(LINE_BREAK), true); break;
      default: out += paragraph('Dialogue', p.lines.map((l) => runs(l)).join(LINE_BREAK), true);
    }
    i += 1;
  }
  if (part.end !== null) out += paragraph('Transition', text(part.end), true);
  return out;
}

/** A UK scene: act and scene on one line, "NAME:" and a tab before each speech, which hangs at 1.5 in. */
function ukPart(part: StagePart, musical = false): string {
  let out = part.headings.length ? paragraph('ActHeading', part.headings.map((h) => text(h, true)).join(TAB), true) : '';
  const continues = continuations(part.paragraphs);
  const ps = part.paragraphs;
  let i = 0;
  while (i < ps.length) {
    const p = ps[i];
    switch (p.element) {
      case 'character': {
        let j = i + 1;
        const speech: string[] = [];
        while (j < ps.length && SPEECH.has(ps[j].element)) {
          const q = ps[j];
          speech.push(q.lines.map((l) => runs(l, q.element === 'parenthetical' || (q.element === 'lyric' && !musical))).join(LINE_BREAK));
          j += 1;
        }
        const name = continues[i] ? '' : text(speaker(fountainParagraphText(p)).toUpperCase() + ':', true);
        out += paragraph(continues[i] ? 'SpeechContinued' : 'Speech', name + TAB + speech.join(text(' ')), true);
        i = j;
        continue;
      }
      case 'action': out += paragraph('StageDirection', p.lines.map((l) => runs(l, true)).join(LINE_BREAK), true); break;
      case 'dialogue': case 'parenthetical': case 'lyric':
        out += paragraph('SpeechContinued', TAB + p.lines.map((l) => runs(l, p.element !== 'dialogue')).join(LINE_BREAK), true);
        break;
      case 'pageBreak': out += PAGE_BREAK; break;
      case 'centered': out += paragraph(isSongHeading(p) ? 'Song' : 'Centred', p.lines.map((l) => runs(l)).join(LINE_BREAK), true); break;
      default: out += paragraph('Transition', p.lines.map((l) => runs(l)).join(LINE_BREAK), true);
    }
    i += 1;
  }
  if (part.end !== null) out += paragraph('Transition', text(part.end), true);
  return out;
}

/** `StageDocx.isSongHeading`: a song's heading, as `sing` made it ("#3 — …"). */
const isSongHeading = (p: Paragraph) => p.lines.length === 1 && /^#\d+ — /.test(fountainParagraphText(p));

/** `StageLayout.bracketed`, for Word. */
function bracketed(lines: Run[][]): Run[][] {
  const t = lines.map((l) => l.map((r) => r.text).join('')).join('\n').replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
  if (!lines.length || t === '' || (t.startsWith('(') && t.endsWith(')'))) return lines;
  const out = lines.map((l) => [...l]);
  out[0].unshift(run('('));
  out[out.length - 1].push(run(')'));
  return out;
}

/** The Word helpers a sitcom's and a radio play's writers share with a play's. */
export const docxBits = {
  TAB, LINE_BREAK, PAGE_BREAK,
  paragraph: (style: string, content: string, spaceAfter = false, pageBreak = false) => paragraph(style, content, spaceAfter, pageBreak),
  text: (s: string, bold = false, italic = false) => text(s, bold, italic),
  runs: (list: Run[], italic = false, stripping: string | null = null) => runs(list, italic, stripping),
  frontMatter: (script: StageScript, m: CompiledManuscript, uk: boolean) => frontMatter(script, m, uk),
  styles: (r: RenderSettings, uk: boolean, language: string, textWidth: number) => styles(r, uk, language, textWidth),
  contentTypes: (headers: number, custom: boolean) => contentTypes(headers, custom),
  rootRels: (custom: boolean) => rootRels(custom),
  documentRels: (headers: number) => documentRels(headers),
};

// MARK: Runs and paragraphs

function paragraph(style: string, content: string, spaceAfter = false, pageBreak = false): string {
  const pPr = `<w:pStyle w:val="${style}"/>` + (pageBreak ? '<w:pageBreakBefore/>' : '') + (spaceAfter ? '<w:spacing w:after="240"/>' : '');
  return `<w:p><w:pPr>${pPr}</w:pPr>${content}</w:p>`;
}

const text = (s: string, bold = false, italic = false) => runs([run(s, { bold, italic })]);

function runs(list: Run[], italic = false, stripping: string | null = null): string {
  return list.map((r) => {
    const t = stripping !== null ? r.text.replaceAll(stripping, '') : r.text;
    const props = (r.bold ? '<w:b/>' : '') + (r.italic !== italic ? '<w:i/>' : '') + (r.underline ? '<w:u w:val="single"/>' : '');
    return `<w:r>${props === '' ? '' : `<w:rPr>${props}</w:rPr>`}<w:t xml:space="preserve">${xmlEscape(t)}</w:t></w:r>`;
  }).join('');
}

// MARK: Parts

function styles(r: RenderSettings, uk: boolean, language: string, textWidth: number): string {
  const size = whole(r.font.size * 2, 2, 3276);
  const font = xmlEscape(r.font.family);
  const style = (id: string, name: string, pPr = '', rPr = '') =>
    `<w:style w:type="paragraph" w:styleId="${id}"><w:name w:val="${name}"/><w:basedOn w:val="Normal"/><w:qFormat/>`
    + (pPr === '' ? '' : `<w:pPr>${pPr}</w:pPr>`) + (rPr === '' ? '' : `<w:rPr>${rPr}</w:rPr>`) + '</w:style>';
  const ind = (l: number, right = 0, hanging = 0) => `<w:ind w:left="${twips(l)}" w:right="${twips(right)}"${hanging > 0 ? ` w:hanging="${twips(hanging)}"` : ''}/>`;
  const tabAt = (inches: number) => `<w:tabs><w:tab w:val="left" w:pos="${twips(inches)}"/></w:tabs>`;
  const centred = '<w:jc w:val="center"/>';
  const keep = '<w:keepNext/>';
  let elements: string[];
  if (uk) {
    elements = [
      style('ActHeading', 'Act Heading', tabAt(1.5) + keep, '<w:caps/>'),
      style('Speech', 'Speech', ind(1.5, 0, 1.5) + tabAt(1.5)),
      style('SpeechContinued', 'Speech Continued', ind(1.5, 0, 1.5) + tabAt(1.5)),
      style('StageDirection', 'Stage Direction', '', '<w:i/>'),
      style('Transition', 'Transition'),
      style('Centred', 'Centred', centred),
      style('Song', 'Song', centred + keep, '<w:b/><w:caps/>'),
    ];
  } else {
    const directionRight = Math.max(0, textWidth - 1.5 - 4.0);
    elements = [
      style('ActHeading', 'Act Heading', ind(2.5) + keep + '<w:spacing w:after="240"/>', '<w:caps/>'),
      style('Character', 'Character', ind(2.5) + keep, '<w:caps/>'),
      style('Dialogue', 'Dialogue'),
      style('Parenthetical', 'Parenthetical', ind(1.5, directionRight) + keep),
      style('StageDirection', 'Stage Direction', ind(1.5, directionRight)),
      style('SettingLabel', 'Setting', ind(1.5, directionRight, 1.5) + tabAt(1.5)),
      style('Lyric', 'Lyric', ind(0.5), '<w:caps/>'),
      style('Transition', 'Transition', ind(2.5), '<w:caps/>'),
      style('Centred', 'Centred', centred),
      style('Song', 'Song', centred + keep, '<w:caps/>'),
    ];
  }
  const front = [
    style('PlayTitle', 'Title', `<w:spacing w:before="${twips(3)}" w:after="240"/>` + centred),
    style('Byline', 'Byline', '<w:spacing w:after="240"/>' + centred),
    style('ContactFirst', 'Contact', `<w:spacing w:before="${twips(2)}"/>`),
    style('Contact', 'Contact Lines'),
    style('PageHeading', 'Page Heading', '<w:spacing w:after="480"/>' + centred),
    style('CastEntry', 'Cast Entry', ind(2.5, 0, 2.5) + tabAt(2.5) + '<w:spacing w:after="240"/>'),
    style('CastNote', 'Cast Note', '<w:spacing w:after="240"/>'),
    style('SettingHeading', 'Setting Heading', '<w:spacing w:after="240"/>' + centred),
    style('SongEntry', 'Song Entry', ind(3.5, 0, 3.5) + tabAt(3.5) + '<w:spacing w:after="240"/>'),
    '<w:style w:type="paragraph" w:styleId="Header"><w:name w:val="header"/><w:basedOn w:val="Normal"/><w:pPr><w:jc w:val="right"/></w:pPr></w:style>',
  ];
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">`
    + `<w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="${font}" w:hAnsi="${font}" w:eastAsia="${font}" w:cs="${font}"/><w:sz w:val="${size}"/><w:szCs w:val="${size}"/><w:lang w:val="${xmlEscape(language === '' ? 'en-AU' : language)}"/></w:rPr></w:rPrDefault>`
    + '<w:pPrDefault><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>'
    + '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>'
    + [...elements, ...front].join('')
    + '</w:styles>';
}

/** The header on the play's pages: its page number at the right, after the act–scene prefix when it has one ("II-1-"). */
function header(prefix: string, period: boolean): string {
  const field = '<w:r><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r><w:r><w:fldChar w:fldCharType="separate"/></w:r><w:r><w:t>1</w:t></w:r><w:r><w:fldChar w:fldCharType="end"/></w:r>';
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
    + `<w:p><w:pPr><w:pStyle w:val="Header"/></w:pPr>${prefix === '' ? '' : text(prefix)}${field}${period ? text('.') : ''}</w:p></w:hdr>`;
}

function contentTypes(headers: number, custom: boolean): string {
  const headerParts = Array.from({ length: headers }, (_, i) =>
    `<Override PartName="/word/header${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>'
    + '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>'
    + '<Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>'
    + headerParts
    + '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>'
    + '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>'
    + (custom ? '<Override PartName="/docProps/custom.xml" ContentType="application/vnd.openxmlformats-officedocument.custom-properties+xml"/>' : '')
    + '</Types>';
}

function rootRels(custom: boolean): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>'
    + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>'
    + '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>'
    + (custom ? '<Relationship Id="rId4" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/custom-properties" Target="docProps/custom.xml"/>' : '')
    + '</Relationships>';
}

function documentRels(headers: number): string {
  const headerRels = Array.from({ length: headers }, (_, i) =>
    `<Relationship Id="rIdHeader${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header${i + 1}.xml"/>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + '<Relationship Id="rIdStyles" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
    + '<Relationship Id="rIdSettings" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>'
    + headerRels
    + '</Relationships>';
}
